import * as React from "react";
import { NavLink } from "react-router";
import {
  Bell,
  Building2,
  FileText,
  Inbox,
  Map,
  MoreHorizontal,
  Settings,
  Sun,
} from "lucide-react";

import { useMe, type PrimaryPersona } from "@/api/me";
import {
  NAV_GROUPS,
  filterNav,
  flattenNav,
  type NavItem,
} from "@/components/layout/nav";
import { MobileBottomSheet } from "@/components/ui/mobile-bottom-sheet";
import { cn } from "@/lib/cn";
import { hapticTap } from "@/native/haptics";
import { useAuthStore } from "@/store/authStore";

/**
 * Bottom nav (MV2-846, app/MobileShells · ds/Nav). 56px plus the safe area,
 * on `--surface`. Operator and owner differ:
 *
 *   Console     Inbox · Sites · Alarms · Map · More
 *   Site Owner  My plants · Reports · Alerts · Settings  (four slots, no More)
 *
 * The active slot takes `--action` and a 600 label — colour, not motion.
 * More lists whatever else the persona owns from the navigation map.
 *
 * MV2-985 — ClientAdmin/SuperAdmin's bar used to swap the fourth slot for
 * "Admin" (`/admin`), which has no route in `router.mobile.tsx` — the tab
 * fell through the router's `*` catch-all and silently bounced to the
 * dashboard. Admin has no mobile surface at all, so all three console
 * personas now share one bar; the "More" sheet already drops Admin (and
 * Simulator/MLOps/Quote) for the same reason via `nav.ts`'s `desktopOnly`.
 */
interface Slot {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
}

const CONSOLE_SLOTS: Slot[] = [
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/sites", label: "Sites", icon: Building2 },
  { to: "/alarms", label: "Alarms", icon: Bell },
  { to: "/map", label: "Map", icon: Map },
];
const OWNER: Slot[] = [
  { to: "/my-plants", label: "My plants", icon: Sun },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/notifications", label: "Alerts", icon: Bell },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function slotsFor(
  persona: PrimaryPersona | null,
): { slots: Slot[]; more: boolean } {
  if (persona === "SiteOwner") return { slots: OWNER, more: false };
  if (persona === "OM" || persona === "ClientAdmin" || persona === "SuperAdmin") {
    return { slots: CONSOLE_SLOTS, more: true };
  }
  return { slots: [], more: false };
}

export function MobileBottomNav(): React.ReactElement | null {
  const [moreOpen, setMoreOpen] = React.useState(false);
  const { data: me } = useMe();
  const acting = useAuthStore((s) => s.actingAs?.clientId != null);
  const persona = me?.primary_persona ?? null;
  const { slots, more } = React.useMemo(() => slotsFor(persona), [persona]);
  const overflow = React.useMemo<NavItem[]>(() => {
    const inBar = new Set(slots.map((s) => s.to));
    return flattenNav(
      filterNav(NAV_GROUPS, persona, acting, me?.features ?? null),
    ).filter((i) => !inBar.has(i.to));
  }, [slots, persona, acting, me?.features]);

  if (persona === null) return null;

  return (
    <>
      <nav
        aria-label="Primary navigation"
        className="fixed inset-x-0 bottom-0 z-40 flex h-[calc(56px+env(safe-area-inset-bottom))] items-stretch border-t border-gray-200 bg-surface pb-[env(safe-area-inset-bottom)]"
      >
        {slots.map((s) => (
          <BottomSlot key={s.to} slot={s} />
        ))}
        {more && (
          <button
            type="button"
            onClick={() => {
              hapticTap();
              setMoreOpen(true);
            }}
            className="flex flex-1 flex-col items-center justify-center gap-[3px] text-[10px] text-gray-500"
            aria-label="More navigation"
          >
            <MoreHorizontal size={18} />
            <span>More</span>
          </button>
        )}
      </nav>
      {more && (
        <MobileBottomSheet
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          title="More"
        >
          <ul className="flex flex-col pb-2">
            {overflow.map((item, index) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => {
                      hapticTap();
                      setMoreOpen(false);
                    }}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 border-gray-100 py-3 text-[14px]",
                        isActive ? "font-medium text-gray-900" : "text-gray-800",
                        index === 0 ? "border-t-0" : "border-t",
                      )
                    }
                  >
                    <Icon size={18} />
                    <span className="flex-1">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </MobileBottomSheet>
      )}
    </>
  );
}

function BottomSlot({ slot }: { slot: Slot }): React.ReactElement {
  const Icon = slot.icon;
  return (
    <NavLink
      to={slot.to}
      onClick={() => hapticTap()}
      className={({ isActive }) =>
        cn(
          "flex flex-1 flex-col items-center justify-center gap-[3px] text-[10px] transition-colors duration-hover",
          isActive
            ? "font-semibold text-[color:var(--action)]"
            : "text-gray-500",
        )
      }
      aria-label={slot.label}
    >
      <Icon size={18} />
      <span>{slot.label}</span>
    </NavLink>
  );
}
